import React, { useMemo, useContext, useCallback } from "react";
import { Card, CardActionArea, CardContent, Typography, Grid2 as Grid, useTheme, useMediaQuery } from "@mui/material";
import PageContext from "../Common/PageContext";
import { useStyles } from "./OutstandingEdiInvoiceCard.styles";
import { OutstandingEdiInvoiceResultLinkProps } from "../Search/types";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import { determineReceiveType } from "../../util/receiveTypeMapping";

const OutstandingEdiInvoiceResultLink: React.FC<OutstandingEdiInvoiceResultLinkProps> = ({
	onSelect,
	receiveType,
	invoiceType,
	purchaseOrderNumber,
	invoiceNumber,
	invoiceDate,
	supplierName,
	searchDataSource,
	totalQuantity,
	totalCostInc,
	daysOverdue,
	status,
	highlight,
	greenMinDays,
	yellowMinDays,
	redMinDays,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	// Color logic matching the tab categories
	const getDaysOverdueColor = () => {
		if (daysOverdue >= redMinDays) return "#ffcdd2"; // Red background for 15+ days (🔴)
		if (daysOverdue >= yellowMinDays) return "#fff9c4"; // Yellow background for 8-14 days (🟡)
		if (daysOverdue >= greenMinDays) return "#c8e6c8"; // Green background for 5-7 days (🟢)
		return "#f5f5f5"; // Light gray background for less than 5 days
	};
	const [pageContext] = useContext(PageContext);

	const {
		userLocation,
		featureConfiguration: { partReceive },
	} = pageContext;

	const isPartReceiveFeatureEnabled = useMemo(() => {
		return isFeatureEnabledForLocation(userLocation, partReceive);
	}, [userLocation, partReceive]);

	const determinedReceiveType = useMemo(() => {
		return determineReceiveType(
			invoiceType,
			receiveType,
			purchaseOrderNumber,
			invoiceNumber,
			status,
			isPartReceiveFeatureEnabled,
			searchDataSource
		);
	}, [
		invoiceType,
		receiveType,
		purchaseOrderNumber,
		invoiceNumber,
		status,
		isPartReceiveFeatureEnabled,
		searchDataSource,
	]);

	const handleSelect = useCallback(() => onSelect(determinedReceiveType), [onSelect, determinedReceiveType]);

	return (
		<Card
			elevation={0}
			square
			className={highlight ? classes.outstandingEdiInvoiceLinkHighlighted : classes.outstandingEdiInvoiceLink}
		>
			<CardActionArea onClick={handleSelect}>
				<CardContent className={classes.compactCardContent}>
					<Grid container spacing={0} alignItems="center" justifyContent="flex-start">
						<Grid size={isMobile ? 8 : 2} className={classes.compactGridItem}>
							<Typography variant="caption" color="textSecondary" display="block" className={classes.titleText}>
								{isMobile ? "Inv No." : "Invoice No."}
							</Typography>
							<Typography variant="body2" className={classes.valueText}>
								{invoiceNumber}
							</Typography>
						</Grid>
						<Grid
							size={isMobile ? 4 : 2}
							className={`${classes.centeredText} ${classes.compactGridItem}`}
							style={isMobile ? { marginLeft: "auto" } : { marginRight: 16 }}
						>
							<div
								className={classes.daysOverdueBox}
								style={{
									backgroundColor: getDaysOverdueColor(),
									borderRadius: 4,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									height: "100%",
								}}
							>
								<Typography
									variant="caption"
									style={{
										fontWeight: "bold",
										padding: isMobile ? "2px 4px" : "4px 8px",
										fontSize: "0.75rem",
										display: "inline",
										whiteSpace: "normal",
										color: "#000000",
									}}
								>
									{`${daysOverdue} DAYS`}
								</Typography>
							</div>
						</Grid>
						<Grid size={isMobile ? 3 : 1} className={classes.compactGridItem}>
							<Typography variant="caption" color="textSecondary" display="block" className={classes.titleText}>
								{isMobile ? "Inv Date" : "Invoice Date"}
							</Typography>
							<Typography variant="body2" className={classes.valueText}>
								{isMobile ? invoiceDate?.split("T")[0].slice(2) : invoiceDate?.split("T")[0]}
							</Typography>
						</Grid>
						<Grid size={isMobile ? 3 : 1} className={classes.compactGridItem}>
							<Typography variant="caption" color="textSecondary" display="block" className={classes.titleText}>
								PO
							</Typography>
							<Typography variant="body2" className={classes.valueText}>
								{purchaseOrderNumber}
							</Typography>
						</Grid>
						<Grid
							size={isMobile ? 2 : 1}
							className={`${classes.centeredText} ${classes.compactGridItem}`}
							style={{ marginRight: isMobile ? 0 : 1 }}
						>
							<Typography variant="caption" color="textSecondary" display="block" className={classes.titleText}>
								Qty
							</Typography>
							<Typography variant="body2" className={classes.valueText}>
								{totalQuantity}
							</Typography>
						</Grid>
						<Grid
							size={isMobile ? 3 : 1}
							className={classes.compactGridItem}
							style={{ marginRight: isMobile ? "auto" : 0 }}
						>
							<Typography variant="caption" color="textSecondary" display="block" className={classes.titleText}>
								Total Inc.
							</Typography>
							<Typography variant="body2" className={classes.valueText}>
								{totalCostInc && `$${totalCostInc.toFixed(2)}`}
							</Typography>
						</Grid>
						<Grid size="auto" className={classes.compactGridItem} style={{ marginRight: isMobile ? 0 : "auto" }}>
							<Typography variant="caption" color="textSecondary" display="block" className={classes.titleText}>
								Supplier
							</Typography>
							<Typography variant="body2" className={classes.valueText}>
								{supplierName}
							</Typography>
						</Grid>
					</Grid>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};

export default OutstandingEdiInvoiceResultLink;
